import { DeleteOutlined } from '@ant-design/icons'
import AddIcon from '@mui/icons-material/Add'
import {
  alpha,
  Box,
  Button,
  FormControl,
  Grid2 as Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
} from '@mui/material'
import SingleOrganizationSelector from 'components/SingleOrganizationSelector'
import useConfig from 'hooks/useConfig'
import { useMemo } from 'react'
import { Permissions } from 'types/permissions'
import { lookupGuestRole, lookupMemberRole, lookupOwnerRole } from 'types/role'
import { getUniqueOrganizationRoles } from 'types/user-profile'
import { useEditUserDialog } from '../context/EditUserDialogContext'

const UserRolesForm: React.FC = () => {
  const { mode } = useConfig()
  const { formData, handleFormDataChange, roles, isLoadingRoles, profile, isLoadingProfile } = useEditUserDialog()

  const isDataLoading = isLoadingProfile || !profile || isLoadingRoles || !roles

  const userOrgRoles = useMemo(() => {
    if (!profile) return []
    return getUniqueOrganizationRoles(profile)
  }, [profile])

  const memberRoleId = useMemo(() => lookupMemberRole(roles)?.id, [roles])
  const guestRoleId = useMemo(() => lookupGuestRole(roles)?.id, [roles])
  const ownerRoleId = useMemo(() => lookupOwnerRole(roles)?.id, [roles])

  const canChangeRoles = useMemo(() => {
    if (isDataLoading) return {}

    const permissions: Record<string, string[]> = {}

    userOrgRoles.forEach((userRole) => {
      const { organization, role } = userRole
      permissions[organization.id] = roles.filter((r) => r.rank >= role.rank).map((r) => r.name)
    })

    return permissions
  }, [isDataLoading, userOrgRoles, roles])

  const hasWritePermissions = useMemo(() => {
    if (!profile) return {}

    const permissions: Record<string, boolean> = {}

    userOrgRoles.forEach((userRole) => {
      const { organization, role } = userRole
      permissions[organization.id] = role.permissions.includes(Permissions.MEMBERS_WRITE)
    })

    return permissions
  }, [profile, userOrgRoles])

  const handleAssignmentChange = (
    index: number,
    field: 'organizationId' | 'roleId' | 'delete',
    value: string | boolean
  ) => {
    const newAssignments = [...formData.assignedRoles]

    if (field === 'delete') {
      newAssignments[index].delete = value as boolean
    } else {
      newAssignments[index][field] = value as string

      // Reset roleId when organizationId changes
      if (field === 'organizationId') {
        newAssignments[index].roleId = ''
      }
    }

    handleFormDataChange({ ...formData, assignedRoles: newAssignments })
  }

  const addAssignment = () => {
    const newAssignments = [
      ...formData.assignedRoles,
      {
        organizationId: '',
        roleId: '',
        delete: false,
      },
    ]
    handleFormDataChange({ ...formData, assignedRoles: newAssignments })
  }

  const toggleAssignmentDeletion = (index: number) => {
    const newAssignments = [...formData.assignedRoles]
    newAssignments[index].delete = !newAssignments[index].delete
    handleFormDataChange({ ...formData, assignedRoles: newAssignments })
  }

  const getAvailableRoles = (organizationId: string, isDisabled: boolean) => {
    if (isDisabled) return roles

    const allowedRoleNames = canChangeRoles[organizationId] || []
    return roles
      .filter((role) => !['Member', 'Guest', 'Owner'].includes(role.name))
      .filter((role) => allowedRoleNames.includes(role.name))
  }

  const shouldHideAssignment = (assignment: any) => {
    return (
      memberRoleId === assignment.roleId ||
      guestRoleId === assignment.roleId ||
      ownerRoleId === assignment.roleId ||
      assignment.delete
    )
  }

  const getAssignmentState = (assignment: any) => {
    const isNewRow = !assignment.delete && (!assignment.organizationId || !assignment.roleId)
    const hasWritePermission = isNewRow || hasWritePermissions[assignment.organizationId]
    const allowedRoles = canChangeRoles[assignment.organizationId] || []
    const currentRole = roles.find((r) => r.id === assignment.roleId)
    const currentRoleAllowed = isNewRow || allowedRoles.includes(currentRole?.name || '')
    const isDisabled = assignment.delete || (!isNewRow && (!hasWritePermission || !currentRoleAllowed))

    return {
      isNewRow,
      isDisabled,
      tooltipTitle: isDisabled ? 'Insufficient permissions or role has been disabled' : '',
    }
  }

  if (isDataLoading) return null

  return (
    <Grid container spacing={3}>
      {formData.assignedRoles.map((assignment, index) => {
        if (shouldHideAssignment(assignment)) return null

        const { isDisabled, tooltipTitle } = getAssignmentState(assignment)
        const availableRoles = getAvailableRoles(assignment.organizationId, isDisabled)

        return (
          <Grid key={index} size={{ xs: 12 }}>
            <Tooltip title={tooltipTitle} arrow>
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'center',
                  opacity: isDisabled ? 0.5 : 1,
                  backgroundColor: isDisabled ? (theme) => alpha(theme.palette.action.disabled, 0.1) : 'transparent',
                }}
              >
                <Box sx={{ width: '100%' }}>
                  <SingleOrganizationSelector
                    userProfile={profile!}
                    selectedOrganizationId={assignment.organizationId}
                    handleChange={(value) => handleAssignmentChange(index, 'organizationId', value)}
                    disabled={isDisabled}
                    label="Team"
                    labelBackgroundColor={mode === 'dark' ? '#747474' : '#ffffff'}
                  />
                </Box>

                <Box sx={{ width: '100%' }}>
                  <FormControl
                    fullWidth
                    variant="outlined"
                    sx={{
                      '& .MuiInputLabel-root': {
                        backgroundColor: mode === 'dark' ? '#747474' : '#ffffff',
                      },
                    }}
                  >
                    <InputLabel shrink variant="outlined">
                      Role
                    </InputLabel>
                    <Select
                      variant="outlined"
                      value={assignment.roleId}
                      onChange={(e) => handleAssignmentChange(index, 'roleId', e.target.value)}
                      label="Role"
                      fullWidth
                      style={{ borderRadius: '21px' }}
                      disabled={isDisabled || !assignment.organizationId}
                    >
                      {assignment.organizationId &&
                        availableRoles.map((role) => (
                          <MenuItem key={role.id} value={role.id}>
                            {role.name}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Box>

                <IconButton
                  onClick={() => toggleAssignmentDeletion(index)}
                  disabled={isDisabled || formData.assignedRoles.length <= 1}
                >
                  <DeleteOutlined style={{ color: isDisabled ? 'disabled' : 'red' }} />
                </IconButton>
              </Box>
            </Tooltip>
          </Grid>
        )
      })}

      <Grid size={{ xs: 12 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={addAssignment} sx={{ mt: 1 }}>
          Add Role
        </Button>
      </Grid>
    </Grid>
  )
}

export default UserRolesForm
